import { Close, Send } from '@icon-park/react';
import { memo, useCallback, useState } from 'react';
import { Collapse } from 'react-collapse';
import { FormattedMessage, useIntl } from 'react-intl';

import { FEEDBACK_CAMPAIGN_ANSWER_COMMENT_MAX_LENGTH } from '@didaskill/domain/feedback-campaigns/constants';
import type { USER_ACCOUNT_TYPES } from '@didaskill/domain/users/constants';
import {
  Button,
  Card,
  Link,
  MOOD_STATES,
  MOODS,
  MoodBox,
  TextArea,
  Typography,
} from '@didaskill/ui/design-system';
import { globalMessages, NON_BREAKING_SPACE } from '@didaskill/ui/intl';
import { ComponentSwitch } from '@didaskill/ui/utils';

import messages, { sayMorePlaceholderMessages, titleMessages } from './MoodTrackerCard.messages';
import * as styles from './MoodTrackerCard.styles';

type MoodTrackerCardBaseProps = {
  onSubmit?: (mood: MOODS, comment: string) => void;
  onRequestClose?: () => void;
  appointmentSchedulingUrl?: string;
};

export type MoodTrackerCardProps =
  | (MoodTrackerCardBaseProps & {
      accountType: USER_ACCOUNT_TYPES.TUTOR;
      fullName: string;
    })
  | (MoodTrackerCardBaseProps & {
      accountType: Exclude<USER_ACCOUNT_TYPES, USER_ACCOUNT_TYPES.TUTOR>;
      fullName?: never;
    });

const MoodTrackerCardBase = ({
  onSubmit,
  onRequestClose,
  appointmentSchedulingUrl,
  accountType,
  fullName,
}: MoodTrackerCardProps) => {
  const [selectedMood, setSelectedMood] = useState<MOODS | null>(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [comment, setComment] = useState('');

  const intl = useIntl();

  const onClickGoBack = useCallback(() => {
    setSelectedMood(null);
  }, []);

  const onClickSend = useCallback(() => {
    if (onSubmit) {
      onSubmit(selectedMood, comment);
    }

    setHasAnswered(true);
  }, [onSubmit, selectedMood, comment]);

  const getMoodState = useCallback(
    (mood: MOODS) => {
      if (!selectedMood) {
        return MOOD_STATES.DEFAULT;
      }
      return mood === selectedMood ? MOOD_STATES.SELECTED : MOOD_STATES.DISABLED;
    },
    [selectedMood],
  );

  return (
    <Card variant={Card.VARIANTS.NEUTRAL} css={styles.moodTrackerCard}>
      <Card.Body css={styles.moodTrackerCardBody}>
        <ComponentSwitch value={hasAnswered}>
          <ComponentSwitch.Item value={false}>
            <div css={styles.header}>
              <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD} css={styles.title}>
                <FormattedMessage
                  {...titleMessages[accountType][selectedMood]}
                  values={{ fullName, nbsp: NON_BREAKING_SPACE }}
                />
              </Typography>

              <div css={styles.moodBoxes}>
                {Object.values(MOODS).map((mood: MOODS) => (
                  <MoodBox
                    key={mood}
                    mood={mood}
                    state={getMoodState(mood)}
                    onClick={setSelectedMood}
                  />
                ))}
              </div>
            </div>

            <div css={styles.collapsedContent}>
              <Collapse isOpened={!!selectedMood}>
                <Typography variant={Typography.VARIANTS.BUTTON_MEDIUM_REGULAR}>
                  <FormattedMessage {...messages.SAY_MORE} />
                </Typography>

                <TextArea
                  css={styles.textarea}
                  placeholder={
                    selectedMood && intl.formatMessage(sayMorePlaceholderMessages[selectedMood])
                  }
                  maxLength={FEEDBACK_CAMPAIGN_ANSWER_COMMENT_MAX_LENGTH}
                  value={comment}
                  onChange={setComment}
                />

                <div css={styles.cardActions}>
                  <Card.Action
                    css={styles.cardAction}
                    variant={Card.Action.VARIANTS.NEUTRAL}
                    onClick={onClickGoBack}
                  >
                    <FormattedMessage {...globalMessages.GO_BACK} />
                  </Card.Action>

                  <Card.Action
                    css={styles.cardAction}
                    variant={Card.Action.VARIANTS.PRIMARY}
                    onClick={onClickSend}
                    icon={<Send />}
                  >
                    <FormattedMessage {...globalMessages.SEND} />
                  </Card.Action>
                </div>
              </Collapse>
            </div>
          </ComponentSwitch.Item>

          <ComponentSwitch.Item value>
            <Button
              icon={<Close />}
              onClick={onRequestClose}
              css={styles.closeButton}
              size={Button.SIZES.SMALL}
            />

            <Typography variant={Typography.VARIANTS.HEADING_4} css={styles.title}>
              <FormattedMessage {...messages.THANKS_FEEDBACK} />
            </Typography>

            {selectedMood !== MOODS.POSITIVE && appointmentSchedulingUrl && (
              <Typography variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR} css={styles.subtitle}>
                <FormattedMessage {...messages.NEGATIVE_FEEDBACK_SUBTITLE} />

                <Link to={appointmentSchedulingUrl} css={styles.appointmentLink}>
                  <FormattedMessage {...messages.SCHEDULE_APPOINTMENT} />
                </Link>
              </Typography>
            )}
          </ComponentSwitch.Item>
        </ComponentSwitch>
      </Card.Body>
    </Card>
  );
};

MoodTrackerCardBase.displayName = 'MoodTrackerCard';

export const MoodTrackerCard = memo(MoodTrackerCardBase);
